import { TicketTypeSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { WizardStep } from "@shared/components/application/navigation/Wizard/WizardStep.web";
import TicketTypesList from "@shared/modules/events/components/tickets/TicketTypesList/TicketTypesList.web";
import {
  isPublicEvent,
  isVenueBasedEvent,
} from "@shared/modules/events/utils/event-utils";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import { useMemo, useState, useEffect } from "react";
import { AnalyticsService } from "@shared/services/analytics";
import { enums } from "@shared/constants";
import { EVENT_WIZARD_SCREENS_MAP } from "../types";
import { PatronTicketTypeSelectionScreenProps } from "./PatronTicketTypeSelectionScreen.props";
/**
 * A modal screen to allow for ticket type selection.
 *
 * Currently uses TicketTypesList internally.
 */
function PatronTicketTypeSelectionScreen({
  onSuccess,
  onDismiss,
  initialSelectedTicketTypes,
  isLoading = false,
  event,
  ...rest
}: PatronTicketTypeSelectionScreenProps) {
  const { t } = useTranslation("common");
  const notifier = NotifierFactory.createNotifier();

  useEffect(() => {
    AnalyticsService.eventViewed(event, {
      label:
        enums.INTERACTION_LABEL_MAP.EVENT_CALENDAR_TICKET_TYPE_SELECTION.id,
    });
  }, [event]);

  const [ticketTypesAndQuantity, setTicketTypesAndQuantity] = useState<
    | Record<
        string,
        {
          name: string;
          quantity: number;
          tt: TicketTypeSerializer;
        }
      >
    | undefined
  >(initialSelectedTicketTypes);

  /**
   * Transforms the ticketTypesAndQuantity (Record<string, {name, quantity, tt}) into a Record<string, number>
   */
  const transformedInitialSelectedTicketTypes = useMemo(() => {
    return Object.values(ticketTypesAndQuantity ?? {}).reduce<
      Record<string, number>
    >((acc, value) => {
      if (!value || !value.tt || value.quantity == null) {
        return acc;
      }

      const { tt, quantity } = value;
      const accCopy = { ...acc };
      accCopy[tt.id] = quantity;
      return accCopy;
    }, {});
  }, [ticketTypesAndQuantity]);

  const popCannotPurchaseAloneMessage = Object.values(
    ticketTypesAndQuantity ?? {}
  ).every((tt) => tt.tt.patron_type?.requires_other_patron_type_on_purchase);

  const { eventData } = rest;

  let ticketTypes = [];
  if (isPublicEvent(eventData)) {
    ticketTypes = eventData.ticket_types;
  } else if (isVenueBasedEvent(eventData)) {
    ticketTypes = eventData.tickettype_set;
  }

  const ttCanbePurchasedAlone = Object.values(ticketTypes ?? {}).filter(
    (tt) => !tt.patron_type?.requires_other_patron_type_on_purchase
  );

  const cannotPurchaseAloneErrorMessage = t(
    `This ticket type requires another ticket type to be purchased with it like ${ttCanbePurchasedAlone
      .map((tt) => tt.name)
      .join(", ")}`
  );

  return (
    <WizardStep
      stepId={EVENT_WIZARD_SCREENS_MAP.TICKET_TYPE_SELECTION.id}
      stepName={t("Tickets")}
    >
      {!isLoading && (
        <>
          <Box
            display="flex"
            flexDirection="column"
            flex="1 1 auto"
            minHeight={0}
            padding={{
              base: "20px 0",
              xs: "xl",
            }}
            backgroundColor="gray.200"
            overflow="auto"
          >
            <TicketTypesList
              {...rest}
              initialSelectedTicketTypes={transformedInitialSelectedTicketTypes}
              onAmountRequested={(tt, amountRequested) => {
                const ttKey = tt.patron_type?.id
                  ? `${tt.patron_type.id}-${tt.patron_option}`
                  : tt.name;

                if (amountRequested && ttKey) {
                  // Add tt to dictionary, keyed by patron id -- not tt id.
                  // This is because attraction parent TTs have no direct connection to child event TTs.
                  // Instead, we just optimistically match a parent TT with a child TT once an event is selected:
                  // User flow: (select parent TT) -> (select child event) -> (find child TT using parent TT & add child TT to cart)
                  setTicketTypesAndQuantity((prev) => ({
                    ...prev,
                    [ttKey]: {
                      name: tt.name,
                      quantity: amountRequested,
                      tt,
                    },
                  }));
                } else {
                  // Delete key/val from dictionary
                  setTicketTypesAndQuantity((prev) => {
                    const prevCopy = { ...prev };
                    if (prevCopy) {
                      delete prevCopy[ttKey];
                    }
                    return prevCopy;
                  });
                }
              }}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            borderTop="1px solid var(--chakra-colors-gray-400)"
            p={{
              base: "16px 24px",
              sm: "16px 20px",
            }}
            id="calendar-modal-footer"
            backgroundColor="bg"
          >
            {onDismiss && (
              <Button
                data-testid="patron-ticket-type-selection-close"
                variant="link"
                color="font.dark"
                onClick={onDismiss}
              >
                {t("Close")}
              </Button>
            )}
            <Button
              data-testid="patron-ticket-type-selection-continue"
              variant="solid"
              colorPalette="primary"
              disabled={!ticketTypesAndQuantity}
              onClick={() => {
                if (popCannotPurchaseAloneMessage) {
                  notifier.error(cannotPurchaseAloneErrorMessage);
                  return;
                }

                // Would never be nullish if disabled conditional term exists
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                onSuccess(ticketTypesAndQuantity!);
              }}
            >
              {t("Continue")}
            </Button>
          </Box>
        </>
      )}
    </WizardStep>
  );
}

export default PatronTicketTypeSelectionScreen;
